import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { Toaster } from "./ui/toaster";
import { Button } from "./ui/button";
import { services, company } from "../mock";
import useReveal from "../hooks/useReveal";

export default function ServicesPage() {
  useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App bg-background">
      <Navbar />

      {/* Blue header band */}
      <header className="relative pt-[64px] sm:pt-[70px] bg-gradient-to-br from-[#08234f] via-[#0b2c63] to-[#0e3576] overflow-hidden">
        <div className="absolute inset-0 circuit-bg opacity-[0.15]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14 sm:py-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sky-200 hover:text-white text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Takaisin etusivulle
          </Link>
          <h1 className="mt-4 font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            Palvelut
          </h1>
          <p className="mt-4 text-slate-200 text-base sm:text-lg max-w-2xl">
            Kattava valikoima sähköpalveluja mittauksista ja kunnossapidosta
            käytönjohtajan vastuisiin. Toimialueena {company.region}.
          </p>
        </div>
      </header>

      {/* Services list */}
      <main className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              const inner = (
                <>
                  <div className="flex items-start justify-between">
                    <span className="grid place-items-center w-14 h-14 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                      <Icon className="w-7 h-7" />
                    </span>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-primary/70 bg-primary/5 rounded-full px-3 py-1">
                      {s.tag}
                    </span>
                  </div>
                  <h3 className="mt-5 font-bold text-lg text-slate-900 leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-1.5 text-sm font-medium text-primary">
                    {s.short}
                  </p>
                  <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
                    {s.description}
                  </p>
                  {s.href && (
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                      Katso asiakkaat
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  )}
                </>
              );
              return s.href ? (
                <Link
                  key={s.id}
                  to={s.href}
                  className="reveal group relative rounded-2xl border border-border bg-white p-6 sm:p-7 block ring-1 ring-primary/20 hover:border-primary/50 hover:shadow-[0_20px_50px_-20px_rgba(20,80,180,0.35)] transition-all"
                  style={{ animationDelay: `${(i % 3) * 60}ms` }}
                >
                  {inner}
                </Link>
              ) : (
                <div
                  key={s.id}
                  className="reveal relative rounded-2xl border border-border bg-white p-6 sm:p-7"
                  style={{ animationDelay: `${(i % 3) * 60}ms` }}
                >
                  {inner}
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-14 rounded-3xl bg-secondary/60 border border-border p-8 sm:p-10 text-center">
            <h2 className="font-extrabold text-2xl sm:text-3xl text-slate-900">
              Tarvitsetko apua sähköiseen haasteeseen?
            </h2>
            <p className="mt-3 text-slate-600 max-w-xl mx-auto">
              Kysy tarjous tai lisätietoja – vastaamme yhteydenottoihin nopeasti.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg" className="rounded-full px-7 h-12 gap-2">
                <a href={`tel:${company.phoneRaw}`}>
                  <Phone className="w-4 h-4" />
                  {company.phone}
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full px-7 h-12 border-slate-300 hover:border-primary hover:text-primary"
              >
                <Link to="/">Etusivulle</Link>
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <Toaster />
    </div>
  );
}
